<template>
  <div class="col-12 mb-1">
    <div class="card w-100 shadow bg-transparent">
      <div
        class="card-body rounded-4"
        :class="`bg-${display.background}`"
      >
        <div
          class="d-flex flex-md-row flex-column justify-content-between"
          style="min-height:80px"
        >
          <div class="d-flex flex-row justify-content-between my-auto fw-bold">
            <div class="d-flex">
              <div class="my-auto text-gray-700">
                {{ payment.startDate | formatDate }}
              </div>
              <div class="d-flex mx-1 mx-md-3">
                <v-icon
                  icon="arrow-right"
                  class="my-auto"
                />
              </div>
              <div class="my-auto text-gray-700">
                {{ payment.endDate | formatDate }}
              </div>
            </div>
            <div
              class="rounded-circle d-flex d-md-none"
              :class="`bg-${isLate ? 'white' : 'blue-600'}`"
              style="width: 48px; height:48px"
            >
              <v-loader-icon
                :icon="isLate ? 'download' : 'download-white'"
                :action="downloadPayment"
                class="m-auto d-flex"
              />
            </div>
          </div>
          <div
            class="d-flex my-md-auto h5"
            :class="{'mb-3': display.addon !== 'checkSuccess'}"
            style="min-width:120px"
          >
            {{ formatPrice }} €
          </div>
          <div class="d-none d-md-flex my-md-auto h5">
            <v-download
              :action="downloadPayment"
              :inverse="isLate"
            />
          </div>
          <div
            class="d-flex my-md-auto"
            style="min-width: 200px"
          >
            <div
              v-if="display.addon === 'checkSuccess'"
              class="btn btn-block text-success d-md-flex d-none"
              style="min-width:140px"
            >
              <font-awesome-icon
                icon="check"
                class="mx-auto"
              />
            </div>
            <div
              v-else-if="display.addon === 'failed'"
              class="btn btn-block text-danger d-md-flex d-none"
              style="min-width:140px"
            >
              <font-awesome-icon
                icon="times"
                class="mx-auto"
              />
            </div>
            <div
              v-else-if="display.addon === 'progress'"
              class="text-blue-500 text-center fw-bold btn btn-block"
              style="min-width:140px"
            >
              En cours
            </div>
            <v-loader
              v-else-if="display.addon === 'advance'"
              :next="advance"
              :height="40"
              class="w-100"
              color="primary"
              title="Payer"
            />
            <v-loader
              v-else-if="display.addon === 'pay'"
              :next="regularization"
              :height="40"
              class="w-100"
              color="pink-700"
              title="Régler"
            />
            <v-loader
              v-else-if="display.addon === 'email'"
              :next="sendEmail"
              :height="40"
              class="w-100"
              color="pink-700"
              title="Contacter"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { formatDate } from '@/utils/_Date';

import DownloadButton from '@/components/common/helpers/DownloadButton.vue';
import LoaderIcon from '@/components/common/helpers/LoaderIcon.vue';
import { mapGetters } from 'vuex';

export default {
  name: 'PaymentDisplay',
  components: {
    'v-download': DownloadButton,
    'v-loader-icon': LoaderIcon,
  },
  filters: {
    formatDate(value) {
      return formatDate(value);
    },
  },
  props: {
    payment: {
      type: Object,
      required: true,
      default: () => ({
        effectiveDate: Date.now(),
        endEffectiveDate: Date.now(),
      }),
    },
  },
  data() {
    return {
      statusDisplay: {
        PAID: {
          background: 'white',
          addon: 'checkSuccess',
        },
        PENDING: {
          background: 'white',
          addon: 'advance',
        },
        IN_PROGRESS: {
          background: 'white',
          addon: 'progress',
        },
        CHECKED_OUT: {
          background: 'white',
          addon: 'advance',
        },
        CANCELLED: {
          background: 'white',
          addon: 'failed',
        },
        FAILED: {
          background: 'red-300',
          addon: 'pay',
        },
        TERMINATED: {
          background: 'white',
          addon: 'failed',
        },
        OVERDUE: {
          background: 'red-300',
          addon: 'pay',
        },
        DISPUTE: {
          background: 'red-300',
          addon: 'pay',
        },
        NON_CONFIE: {
          background: 'red-300',
          addon: 'email',
        },
      },
    };
  },
  computed: {
    ...mapGetters({
      relaunch: 'payments/paymentMethod',
      focusScreen: 'ui/focusScreen',
    }),
    display() {
      if (this.payment.confie) {
        return this.statusDisplay[this.payment.status];
      }
      if (this.payment.status === 'PAID') {
        return this.statusDisplay.PAID;
      }
      return this.statusDisplay.NON_CONFIE;
    },
    formatPrice() {
      const price = this.payment.totalToPay;
      return parseFloat(price || 0)
        .toFixed(2)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
        .replace('.', ',');
    },
    isLate() {
      return ['OVERDUE', 'FAILED', 'DISPUTE'].includes(this.payment.status);
    },
  },
  methods: {
    async dispatchEventsForPayment(type) {
      const query = { reference: this.payment.reference };

      if (this.payment.relaunchId !== null) {
        const { transactionId } = this.payment;

        if (!transactionId || transactionId === 'null' || transactionId === null || transactionId === undefined) {
          try {
            const { id } = await this.$store.dispatch('checkout/createTransactionFromPaymentId', this.payment.id);
            const path = `/client/transaction/${id}`;
            return this.$router.push({ path, query });
          } catch (error) {
            console.error('Failed to create transaction from payment ID:', error);
            const path = `/client/payment/${this.payment.id}/${type}`;
            return this.$router.push({ path, query });
          }
        } else {
          const path = `/client/transaction/${transactionId}`;
          return this.$router.push({ path, query });
        }
      }

      if (this.transactionId) {
        const path = `/client/payment/${this.payment.transactionId}/${type}`;
        return this.$router.push({ path, query });
      }

      const { id } = await this.$store.dispatch('checkout/createTransactionFromPaymentId', this.payment.id);
      const path = `/client/payment/${id}/${type}`;
      return this.$router.push({ path, query });
    },
    regularization() {
      return this.dispatchEventsForPayment('regularization');
    },
    advance() {
      return this.dispatchEventsForPayment('advance');
    },
    downloadPayment() {
      return this.$store.dispatch('download/downloadPaymentPreview', this.payment.id);
    },
    sendEmail() {
      window.open('mailto:gestion@assurup.com');
      return Promise.resolve();
    },
  },
};
</script>
